// Lib
import { useEffect } from 'react';
import { sum } from 'lodash';
import Handsontable from 'handsontable/base';

// Utils
import { resizeTable } from '../utils/tableSizeUtils';
import { manuallySetTableElementWidthPx } from '../utils/tableDOMUtils';
import { RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';
import usePrevious from '../../../utils/react/usePrevious';
import { getHotColWidthsGu } from '../utils/hotInstanceUtils/hotTableGridDimensionsUtils';
import { getHotTableData } from '../utils/hotInstanceUtils/hotTableDataUtilts';
import { TableChangeSource } from '../../../../common/table/TableTypes';

interface UseTableResizeHandlersProps {
    elementId: string;
    gridSize: number;
    isResizing: boolean;
    tempSize: RectSize | null;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    getContextZoomScale: () => number;
    dispatchUpdateTableElement: (args: object) => void;
}

/**
 * Custom hook for handling table resize operations with drag handle
 *
 * @param props - Hook configuration
 * @returns Handler functions for table resizing
 */
export const useTableResizeEffects = (props: UseTableResizeHandlersProps) => {
    const {
        elementId,
        gridSize,
        isResizing,
        tempSize,
        hotTableInstanceRef,
        getContextZoomScale,
        dispatchUpdateTableElement,
    } = props;

    const prevIsResizing = usePrevious(isResizing);

    // Adjust columns and rows with drag handle
    const throttledAdjustTableSize = useThrottledCallback(
        (): void => {
            if (!isResizing || !tempSize || !hotTableInstanceRef.current) return;

            const hot = hotTableInstanceRef.current;
            if (!hot || hot.isDestroyed) return;

            const currentColWidths = getHotColWidthsGu(hotTableInstanceRef.current, gridSize) || [];
            const { rowDif, columnDif } =
                resizeTable(hot, currentColWidths, tempSize, gridSize, getContextZoomScale) ?? {};

            const source = TableChangeSource.RESIZE_HANDLE;
            const nRows = hot.countRows();
            const nCols = hot.countCols();

            if (!rowDif && !columnDif) return;

            // When adjusting table size, alter both rows and columns in 1 batched render
            hot?.batch(() => {
                // Adjust rows
                if (rowDif > 0) {
                    hot.alter('insert_row_below', nRows, rowDif, source);
                } else if (rowDif < 0) {
                    const numberToRemove = -rowDif;
                    hot.alter('remove_row', nRows - numberToRemove, numberToRemove, source);
                }

                // For column changes we also need to update column and table widths
                if (!columnDif) return;

                // Adjust columns
                if (columnDif > 0) {
                    hot.alter('insert_col_end', nCols, columnDif, source);
                } else if (columnDif < 0) {
                    const numberToRemove = -columnDif;
                    hot.alter('remove_col', nCols - numberToRemove, numberToRemove, source);
                }

                // Adjust table width
                const newColWidthsGu = getHotColWidthsGu(hotTableInstanceRef.current, gridSize);
                const newTableWidthGu = sum(newColWidthsGu);
                const newTableWidthPx = newTableWidthGu * gridSize - 2;
                manuallySetTableElementWidthPx(hot, newTableWidthPx);
            });
        },
        20,
        [isResizing, tempSize, hotTableInstanceRef, gridSize, getContextZoomScale],
    );

    // When resizing is done, update the table element
    useEffect(() => {
        if (prevIsResizing && !isResizing) {
            const newData = getHotTableData(hotTableInstanceRef.current);
            const newColWidthsGu = getHotColWidthsGu(hotTableInstanceRef.current, gridSize);

            dispatchUpdateTableElement({
                id: elementId,
                changes: { tableContent: { data: newData, colWidthsGU: newColWidthsGu } },
            });
        }
    }, [isResizing]);

    // When tempSize changes, throttle adjust table size
    useEffect(() => {
        throttledAdjustTableSize();
    }, [tempSize]);
};

export default useTableResizeEffects;
